// Curated ElevenLabs voices for the AI receptionist, 10 per gender.
// The `id` is the ElevenLabs voice ID, passed as-is to Twilio ConversationRelay
// (`<ConversationRelay voice="...">`), so whatever previews here is exactly
// what callers hear.

import { supabase } from './supabase';

export type VoiceGender = 'male' | 'female';

export type ReceptionistVoice = {
  id: string;
  name: string;
  tagline: string;
  gender: VoiceGender;
  /** Stable public preview mp3. May be refreshed at runtime via fetchVoicePreviews(). */
  previewUrl?: string;
};

const GCS = 'https://storage.googleapis.com/eleven-public-prod/premade/voices';

export const RECEPTIONIST_VOICES: ReceptionistVoice[] = [
  // ---- Female ----
  {
    id: '21m00Tcm4TlvDq8ikWAM',
    name: 'Rachel',
    tagline: 'Calm, friendly, all-purpose',
    gender: 'female',
    previewUrl: `${GCS}/21m00Tcm4TlvDq8ikWAM/df6788f9-5c96-470d-8312-aab3b3d8f50a.mp3`,
  },
  {
    id: 'EXAVITQu4vr4xnSDxMaL',
    name: 'Sarah',
    tagline: 'Mature, reassuring, confident',
    gender: 'female',
    previewUrl: `${GCS}/EXAVITQu4vr4xnSDxMaL/01a3e33c-6e99-4ee7-8543-ff2216a32186.mp3`,
  },
  {
    id: 'hpp4J3VqNfWAUOO0d1Us',
    name: 'Bella',
    tagline: 'Professional, bright, warm',
    gender: 'female',
    previewUrl: `${GCS}/hpp4J3VqNfWAUOO0d1Us/dab0f5ba-3aa4-48a8-9fad-f138fea1126d.mp3`,
  },
  {
    id: 'XrExE9yKIg1WjnnlVkGX',
    name: 'Matilda',
    tagline: 'Knowledgeable, upbeat, professional',
    gender: 'female',
    previewUrl: `${GCS}/XrExE9yKIg1WjnnlVkGX/b930e18d-6b4d-466e-bab2-0ae97c6d8535.mp3`,
  },
  {
    id: 'cgSgspJ2msm6clMCkdW9',
    name: 'Jessica',
    tagline: 'Playful, bright, welcoming',
    gender: 'female',
    previewUrl: `${GCS}/cgSgspJ2msm6clMCkdW9/56a97bf8-b69b-448f-846c-c3a11683d45a.mp3`,
  },
  {
    id: 'FGY2WhTYpPnrIDTdsKH5',
    name: 'Laura',
    tagline: 'Enthusiastic with personality',
    gender: 'female',
    // No stable static preview; filled in at runtime by fetchVoicePreviews().
  },
  {
    id: 'Xb7hH8MSUJpSbSDYk0k2',
    name: 'Alice',
    tagline: 'Clear and engaging — British',
    gender: 'female',
    previewUrl: `${GCS}/Xb7hH8MSUJpSbSDYk0k2/d10f7534-11f6-41fe-a012-2de1e482d336.mp3`,
  },
  {
    id: 'pFZP5JQG7iQjIQuC4Bku',
    name: 'Lily',
    tagline: 'Velvety and confident — British',
    gender: 'female',
    previewUrl: `${GCS}/pFZP5JQG7iQjIQuC4Bku/89b68b35-b3dd-4348-a84a-a3c13a3c2b30.mp3`,
  },
  {
    id: 'XB0fDUnXU5powFXDhCwa',
    name: 'Charlotte',
    tagline: 'Smooth and relaxed',
    gender: 'female',
    previewUrl: `${GCS}/XB0fDUnXU5powFXDhCwa/942356dc-f10d-4d89-bda5-4f8505ee038b.mp3`,
  },
  {
    id: '9BWtsMINqrJLrRacOk9x',
    name: 'Aria',
    tagline: 'Expressive with a slight husk',
    gender: 'female',
    previewUrl: `${GCS}/9BWtsMINqrJLrRacOk9x/405766b8-1f4e-4d3c-aba1-6f25333823ec.mp3`,
  },

  // ---- Male ----
  {
    id: 'pNInz6obpgDQGcFmaJgB',
    name: 'Adam',
    tagline: 'Deep, firm, authoritative',
    gender: 'male',
    previewUrl: `${GCS}/pNInz6obpgDQGcFmaJgB/d6905d7a-dd26-4187-bfff-1bd3a5ea7cac.mp3`,
  },
  {
    id: 'nPczCjzI2devNBz1zQrb',
    name: 'Brian',
    tagline: 'Deep, resonant, comforting',
    gender: 'male',
  },
  {
    id: 'cjVigY5qzO86Huf0OWal',
    name: 'Eric',
    tagline: 'Smooth and trustworthy',
    gender: 'male',
    previewUrl: `${GCS}/cjVigY5qzO86Huf0OWal/d098fda0-6456-4030-b3d8-63aa048c9070.mp3`,
  },
  {
    id: 'iP95p4xoKVk53GoZ742B',
    name: 'Chris',
    tagline: 'Charming and down-to-earth',
    gender: 'male',
    previewUrl: `${GCS}/iP95p4xoKVk53GoZ742B/3f4bde72-cc48-40dd-829f-57fbf906f4d7.mp3`,
  },
  {
    id: 'bIHbv24MWmeRgasZH58o',
    name: 'Will',
    tagline: 'Relaxed and optimistic',
    gender: 'male',
    previewUrl: `${GCS}/bIHbv24MWmeRgasZH58o/8caf8f3d-ad29-4980-af41-53f20c72d7a4.mp3`,
  },
  {
    id: 'CwhRBWXzGAHq8TQ4Fs17',
    name: 'Roger',
    tagline: 'Laid-back and classy',
    gender: 'male',
    previewUrl: `${GCS}/CwhRBWXzGAHq8TQ4Fs17/58ee3ff5-f6f2-4628-93b8-e38eb31806b0.mp3`,
  },
  {
    id: 'TX3LPaxmHKxFdv7VOQHJ',
    name: 'Liam',
    tagline: 'Energetic and confident',
    gender: 'male',
    previewUrl: `${GCS}/TX3LPaxmHKxFdv7VOQHJ/63148076-6363-42db-aea8-31424308b92c.mp3`,
  },
  {
    id: 'JBFqnCBsd6RMkjVDRZzb',
    name: 'George',
    tagline: 'Warm storyteller — British',
    gender: 'male',
  },
  {
    id: 'onwK4e9ZLuTAKqWW03F9',
    name: 'Daniel',
    tagline: 'Steady and formal — British',
    gender: 'male',
  },
  {
    id: 'IKne3meq5aSn9XLyUdCD',
    name: 'Charlie',
    tagline: 'Deep and energetic — Australian',
    gender: 'male',
  },
];

// OpenAI Realtime voices (speech-to-speech engine). The `id` is the voice
// name the Realtime API and the TTS preview API both accept. Gender labels
// are by common perception — OpenAI doesn't tag them officially.
export const OPENAI_VOICES: ReceptionistVoice[] = [
  // ---- Female ----
  { id: 'marin', name: 'Marin', tagline: 'Natural, warm, professional — newest', gender: 'female' },
  { id: 'coral', name: 'Coral', tagline: 'Bright and friendly', gender: 'female' },
  { id: 'sage', name: 'Sage', tagline: 'Calm and reassuring', gender: 'female' },
  { id: 'shimmer', name: 'Shimmer', tagline: 'Clear and upbeat', gender: 'female' },
  { id: 'alloy', name: 'Alloy', tagline: 'Smooth and neutral', gender: 'female' },
  // ---- Male ----
  { id: 'cedar', name: 'Cedar', tagline: 'Natural, relaxed, confident — newest', gender: 'male' },
  { id: 'ash', name: 'Ash', tagline: 'Warm and grounded', gender: 'male' },
  { id: 'echo', name: 'Echo', tagline: 'Steady and clear', gender: 'male' },
  { id: 'verse', name: 'Verse', tagline: 'Expressive and engaging', gender: 'male' },
  { id: 'ballad', name: 'Ballad', tagline: 'Soft-spoken and easygoing', gender: 'male' },
];

export type VoiceEngine = 'elevenlabs' | 'openai';

export function voicesForGender(gender: VoiceGender, engine: VoiceEngine = 'elevenlabs'): ReceptionistVoice[] {
  const list = engine === 'openai' ? OPENAI_VOICES : RECEPTIONIST_VOICES;
  return list.filter((v) => v.gender === gender);
}

export function findVoice(
  voiceId: string | undefined,
  engine: VoiceEngine = 'elevenlabs',
): ReceptionistVoice | undefined {
  if (!voiceId) return undefined;
  const list = engine === 'openai' ? OPENAI_VOICES : RECEPTIONIST_VOICES;
  return list.find((v) => v.id === voiceId);
}

let previewsPromise: Promise<Record<string, string>> | null = null;

/**
 * Fetches fresh preview mp3 URLs from the public ElevenLabs voices API
 * (no key required, CORS-open). Some voices only have short-lived signed
 * preview URLs, so we refresh at runtime instead of hardcoding them.
 * Falls back to the static URLs above when the API is unreachable.
 */
export function fetchVoicePreviews(): Promise<Record<string, string>> {
  if (!previewsPromise) {
    previewsPromise = fetch('https://api.elevenlabs.io/v1/voices')
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((data: { voices?: Array<{ voice_id: string; preview_url?: string }> }) => {
        const map: Record<string, string> = {};
        for (const v of data.voices || []) {
          if (v.voice_id && v.preview_url) map[v.voice_id] = v.preview_url;
        }
        return map;
      })
      .catch(() => {
        previewsPromise = null; // allow a retry on next call
        return {};
      });
  }
  return previewsPromise;
}

const dynamicPreviewCache = new Map<string, Promise<string | null>>();
// When generation fails (e.g. no API key yet), back off briefly instead of
// disabling for the whole session — the key may be added at any moment.
let dynamicPreviewsDisabledUntil = 0;

export type VoicePreviewSettings = {
  engine: VoiceEngine;
  expressiveness: number;
  speed: number;
  polish: number;
};

/**
 * Generates a preview through the agent-voice-preview edge function using the
 * same ElevenLabs model + settings math real calls use, so the sample
 * reflects the voice sliders. Returns an object URL, or null when
 * unavailable (no ELEVENLABS_API_KEY configured, or TTS failure) — callers
 * should fall back to the stock samples.
 */
export function generateVoicePreview(
  voiceId: string,
  settings: VoicePreviewSettings,
): Promise<string | null> {
  if (Date.now() < dynamicPreviewsDisabledUntil) return Promise.resolve(null);
  const { engine, expressiveness, speed, polish } = settings;
  // Bucket to 0.05 steps so slider nudges reuse the cache.
  const key = [
    engine,
    voiceId,
    Math.round(expressiveness * 20),
    Math.round(speed * 20),
    Math.round(polish * 20),
  ].join(':');
  let promise = dynamicPreviewCache.get(key);
  if (!promise) {
    promise = (async () => {
      try {
        const { data, error } = await supabase.functions.invoke('agent-voice-preview', {
          body: { engine, voice_id: voiceId, expressiveness, speed, polish },
        });
        if (error) {
          console.warn('Voice preview generation failed, using stock sample:', error);
          const status = (error as { context?: { status?: number } }).context?.status;
          // 503 = no API key configured; back off for a minute, then retry.
          if (status === 503) dynamicPreviewsDisabledUntil = Date.now() + 60_000;
          return null;
        }
        if (!(data instanceof Blob)) return null;
        // The function sends octet-stream (see its comment); tag as mp3 for <audio>.
        return URL.createObjectURL(new Blob([data], { type: 'audio/mpeg' }));
      } catch {
        return null;
      }
    })();
    dynamicPreviewCache.set(key, promise);
    promise.then((url) => {
      if (!url) dynamicPreviewCache.delete(key); // don't cache failures
    });
  }
  return promise;
}
